import { ChartConfig, ChartContainer, ChartTooltip } from "design-system/molecules/Chart"
import { ChartCard } from "design-system/organisms/insights/ChartCard"
import { formatExperimentLatency } from "modules/backoffice/lib/experiments/formatExperimentLatency"
import { getTextWidth } from "modules/shared/lib/charts"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from "recharts"
import {
  BackofficeConversationExperimentAnalyticsResponseDataSchema,
  ConversationExperimentProductionName,
} from "schema"
import { z } from "zod"

type Analytics = z.infer<typeof BackofficeConversationExperimentAnalyticsResponseDataSchema>

type Props = {
  data: Analytics | undefined
  isPending: boolean
}

type MetricKey =
  | "avgAssistantMessages"
  | "avgLatencySeconds"
  | "ticketsRate"
  | "builtInCapabilitiesSuccessRate"
  | "customCapabilitiesSuccessRate"

type ChartRow = {
  label: string
  conversationCount: number
  avgAssistantMessages: number
  avgLatencySeconds: number
  ticketsRate: number
  builtInCapabilitiesSuccessRate: number
  customCapabilitiesSuccessRate: number
}

const METRIC_KEYS = [
  "avgAssistantMessages",
  "avgLatencySeconds",
  "ticketsRate",
  "builtInCapabilitiesSuccessRate",
  "customCapabilitiesSuccessRate",
] as const satisfies readonly MetricKey[]

const METRIC_COLORS: Record<MetricKey, string> = {
  avgAssistantMessages: "var(--chart-2)",
  avgLatencySeconds: "var(--testing-2)",
  ticketsRate: "var(--support-primary)",
  builtInCapabilitiesSuccessRate: "var(--success)",
  customCapabilitiesSuccessRate: "var(--warning)",
}

const ROW_HEIGHT = 44
const MAX_CHART_SCROLL_HEIGHT = 320
const MIN_BAR_OPACITY = 0.4
const MAX_BAR_OPACITY = 1
const LABEL_FONT = "12px Inter, sans-serif"
const MAX_Y_AXIS_WIDTH = 520
const DOMAIN_PADDING = 1.25
const CHART_MARGIN = { left: 0, right: 56, top: 8, bottom: 8 } as const
const LABEL_COLUMN_PADDING_RIGHT = 40
const MIN_METRIC_CHART_WIDTH = 160

function getBarOpacity(index: number, total: number) {
  if (total <= 1) return MAX_BAR_OPACITY
  return MAX_BAR_OPACITY - (index / (total - 1)) * (MAX_BAR_OPACITY - MIN_BAR_OPACITY)
}

function formatBarLabel(metric: MetricKey, value: number) {
  switch (metric) {
    case "avgLatencySeconds":
      return `${value.toFixed(1)}s`
    case "ticketsRate":
    case "builtInCapabilitiesSuccessRate":
    case "customCapabilitiesSuccessRate":
      return `${value.toFixed(1)}%`
    case "avgAssistantMessages":
      return value.toFixed(1)
  }
}

function getMetricValue(row: ChartRow, metric: MetricKey) {
  return row[metric]
}

function compareExperimentRows(left: ChartRow, right: ChartRow) {
  if (left.label === ConversationExperimentProductionName) return -1
  if (right.label === ConversationExperimentProductionName) return 1
  return right.label.localeCompare(left.label)
}

function TooltipMetricRow({
  color,
  label,
  value,
}: {
  color: string
  label: string
  value: string
}) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="size-2 shrink-0 rounded-full" style={{ background: color }} />
      <span>{`${label}: ${value}`}</span>
    </div>
  )
}

export function BackofficeConversationExperimentAnalyticsChart({ data, isPending }: Props) {
  const { t } = useTranslation()

  const noData = !isPending && !data?.length

  const chartData = useMemo(() => {
    return (data ?? []).map((item) => ({
      label: item.experiment,
      conversationCount: item.conversationCount,
      avgAssistantMessages: Number(item.avgAssistantMessages.toFixed(1)),
      avgLatencySeconds:
        item.avgLatencySeconds != null ? Number(item.avgLatencySeconds.toFixed(1)) : 0,
      ticketsRate: Number(item.ticketsRate.toFixed(1)),
      builtInCapabilitiesSuccessRate: Number(item.builtInCapabilitiesSuccessRate.toFixed(1)),
      customCapabilitiesSuccessRate: Number(item.customCapabilitiesSuccessRate.toFixed(1)),
    }))
  }, [data])

  const sortedChartData = useMemo(() => {
    return [...chartData].sort(compareExperimentRows)
  }, [chartData])

  const metricMaxByKey = useMemo(() => {
    return Object.fromEntries(
      METRIC_KEYS.map((metric) => [
        metric,
        Math.max(...sortedChartData.map((row) => getMetricValue(row, metric)), 1) * DOMAIN_PADDING,
      ]),
    ) as Record<MetricKey, number>
  }, [sortedChartData])

  const yAxisWidth = useMemo(() => {
    if (!sortedChartData.length) return 100
    const max = Math.max(...sortedChartData.map((item) => getTextWidth(item.label, LABEL_FONT)))
    return Math.min(Math.ceil(max), MAX_Y_AXIS_WIDTH)
  }, [sortedChartData])

  const chartConfig = useMemo(() => {
    return {
      avgAssistantMessages: {
        label: t("backoffice.conversationExperiments.chart.avgAssistantMessages"),
        color: METRIC_COLORS.avgAssistantMessages,
      },
      avgLatencySeconds: {
        label: t("backoffice.conversationExperiments.chart.avgLatency"),
        color: METRIC_COLORS.avgLatencySeconds,
      },
      ticketsRate: {
        label: t("backoffice.conversationExperiments.chart.ticketsRate"),
        color: METRIC_COLORS.ticketsRate,
      },
      builtInCapabilitiesSuccessRate: {
        label: t("backoffice.conversationExperiments.chart.builtInCapabilitiesSuccessRate"),
        color: METRIC_COLORS.builtInCapabilitiesSuccessRate,
      },
      customCapabilitiesSuccessRate: {
        label: t("backoffice.conversationExperiments.chart.customCapabilitiesSuccessRate"),
        color: METRIC_COLORS.customCapabilitiesSuccessRate,
      },
    } satisfies ChartConfig
  }, [t])

  const chartHeight = sortedChartData.length * ROW_HEIGHT + CHART_MARGIN.top + CHART_MARGIN.bottom

  const tooltipContent = ({
    active,
    payload,
  }: {
    active?: boolean
    payload?: ReadonlyArray<{ payload?: ChartRow }>
  }) => {
    const row = payload?.[0]?.payload
    if (!active || !row) return null

    return (
      <div className="rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
        <div className="font-medium text-foreground">
          {t("backoffice.conversationExperiments.chart.tooltipLabel", {
            experiment: row.label,
            count: row.conversationCount,
          })}
        </div>
        <div className="mt-1 flex flex-col gap-0.5 text-muted-foreground">
          <TooltipMetricRow
            color={METRIC_COLORS.avgAssistantMessages}
            label={t("backoffice.conversationExperiments.chart.avgAssistantMessages")}
            value={String(row.avgAssistantMessages)}
          />
          <TooltipMetricRow
            color={METRIC_COLORS.avgLatencySeconds}
            label={t("backoffice.conversationExperiments.chart.avgLatency")}
            value={formatExperimentLatency(row.avgLatencySeconds || null)}
          />
          <TooltipMetricRow
            color={METRIC_COLORS.ticketsRate}
            label={t("backoffice.conversationExperiments.chart.ticketsRate")}
            value={`${row.ticketsRate}%`}
          />
          <TooltipMetricRow
            color={METRIC_COLORS.builtInCapabilitiesSuccessRate}
            label={t("backoffice.conversationExperiments.chart.builtInCapabilitiesSuccessRate")}
            value={`${row.builtInCapabilitiesSuccessRate}%`}
          />
          <TooltipMetricRow
            color={METRIC_COLORS.customCapabilitiesSuccessRate}
            label={t("backoffice.conversationExperiments.chart.customCapabilitiesSuccessRate")}
            value={`${row.customCapabilitiesSuccessRate}%`}
          />
        </div>
      </div>
    )
  }

  const legend = (
    <div className="flex flex-wrap items-center justify-end gap-4">
      {METRIC_KEYS.map((metric) => (
        <div key={metric} className="flex items-center gap-1.5">
          <div
            className="size-2 shrink-0 rounded-full"
            style={{ background: METRIC_COLORS[metric] }}
          />
          <span className="text-sm text-muted-foreground">{chartConfig[metric].label}</span>
        </div>
      ))}
    </div>
  )

  const chart = (
    <div
      className="flex overflow-y-auto"
      style={{
        maxHeight: MAX_CHART_SCROLL_HEIGHT,
        minWidth:
          yAxisWidth + LABEL_COLUMN_PADDING_RIGHT + METRIC_KEYS.length * MIN_METRIC_CHART_WIDTH,
      }}
    >
      <div
        className="flex shrink-0 flex-col"
        style={{
          width: yAxisWidth + LABEL_COLUMN_PADDING_RIGHT,
          height: chartHeight,
          paddingTop: CHART_MARGIN.top,
          paddingBottom: CHART_MARGIN.bottom,
          paddingRight: LABEL_COLUMN_PADDING_RIGHT,
        }}
      >
        {sortedChartData.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-end text-right text-xs leading-tight text-foreground"
            style={{ height: `${100 / Math.max(sortedChartData.length, 1)}%` }}
            title={row.label}
          >
            <span className="line-clamp-2 break-words">{row.label}</span>
          </div>
        ))}
      </div>

      <div className="flex min-w-0 flex-1 gap-1">
        {METRIC_KEYS.map((metric) => (
          <ChartContainer
            key={metric}
            config={chartConfig}
            className="mx-auto min-w-0 w-full flex-1 overflow-visible"
            style={{ height: chartHeight }}
          >
            <BarChart
              accessibilityLayer
              data={sortedChartData}
              layout="vertical"
              margin={CHART_MARGIN}
            >
              <YAxis dataKey="label" type="category" hide width={0} />
              <XAxis type="number" hide domain={[0, metricMaxByKey[metric]]} />
              <ChartTooltip cursor={false} content={tooltipContent} />
              <Bar
                dataKey={metric}
                name={chartConfig[metric].label}
                fill={METRIC_COLORS[metric]}
                maxBarSize={28}
                radius={4}
              >
                {sortedChartData.map((_, index) => (
                  <Cell
                    key={index}
                    fill={METRIC_COLORS[metric]}
                    fillOpacity={getBarOpacity(index, sortedChartData.length)}
                  />
                ))}
                <LabelList
                  dataKey={metric}
                  position="right"
                  offset={8}
                  className="fill-accent-foreground"
                  fontSize={12}
                  formatter={(value) => formatBarLabel(metric, Number(value))}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        ))}
      </div>
    </div>
  )

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-[600px]">
        <ChartCard
          title={t("backoffice.conversationExperiments.chart.title")}
          description={t("backoffice.conversationExperiments.chart.description")}
          headerAction={legend}
          chart={chart}
          isPending={isPending}
          autoHeight
          shouldScrollToEnd={false}
          noData={{
            show: noData,
            title: t("backoffice.conversationExperiments.chart.empty"),
            description: t("backoffice.conversationExperiments.chart.emptyDescription"),
          }}
        />
      </div>
    </div>
  )
}
